import { PropsWithChildren, useContext, useRef, useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '@ghq-abi/design-system-icons';

import { CollapsibleContext } from '../Root';

type CollapsibleItem = PropsWithChildren<{
  label: string;
  isFirst?: boolean;
  isLast?: boolean;
}>;

export function Item({ label, children, isFirst, isLast }: CollapsibleItem) {
  const ref = useRef<HTMLDivElement>(null);

  const { active, toggle } = useContext(CollapsibleContext);
  const isOpen = active.includes(label);
  const [height, setHeight] = useState<string | number>(isOpen ? 'auto' : 0);

  const handleToggle = () => {
    const el = ref.current;

    if (!el) {
      return;
    }

    if (isOpen) {
      const startHeight = el.scrollHeight;
      setHeight(startHeight);

      requestAnimationFrame(() => {
        setHeight(0);
      });
    } else {
      const endHeight = el.scrollHeight;

      setHeight(endHeight);
    }

    toggle(label);
  };

  const onTransitionEnd = () => {
    if (isOpen) {
      setHeight('auto');
    }
  };

  return (
    <div
      className={`border overflow-hidden ${isFirst ? 'rounded-t-md' : ''} ${isLast ? 'rounded-b-md' : ''}`}>
      <div className="h-[72px] bg-[var(--theme-color-background-dark)] flex gap-2 pb-2">
        <button
          aria-expanded={isOpen}
          aria-label={label}
          type="button"
          onClick={handleToggle}
          className="flex-1 text-left flex items-center pl-6 font-semibold">
          {label}
        </button>

        <button
          aria-expanded={isOpen}
          aria-label={isOpen ? 'Fechar' : 'Abrir'}
          type="button"
          onClick={handleToggle}
          className="pr-6">
          {isOpen ? (
            <ChevronUpIcon width={16} height={16} />
          ) : (
            <ChevronDownIcon width={16} height={16} />
          )}
        </button>
      </div>

      <div
        ref={ref}
        style={{ height }}
        onTransitionEnd={onTransitionEnd}
        className="overflow-hidden transition-[height_300ms_ease]">
        <div
          className={`p-6 bg-white flex flex-col gap-4 ${isLast ? 'rounded-b-md' : ''}`}>
          {children}
        </div>
      </div>
    </div>
  );
}
